// Preis-Rollout S3: gemeinsame Anzeige-Bausteine aller Rollout-Fenster (Seite /preise/rollout und Grundartikel).
// Ein Artikel sieht überall gleich aus: Sternnummer groß, DR-Nummer klein, Einzelstück/×n/Set, Grundartikel.
import { GrundartikelLink } from '@/components/catalog/GrundartikelLink'
import type { Kennung, ScopeWirkung, ShippingPolicy, Zeitplan } from '@/api/priceRollout'
import { setText, wirkungText, zeitplanText } from '@/lib/rollout-klartext'

function Stern({ stern, gross }: { stern: string; gross: boolean }) {
  // Der Stern ist Präfix, die Ziffern sind die Nummer: Präfix leicht, Ziffern schwer.
  const ziffern = stern.replace(/^\*+/, '')
  const praefix = stern.slice(0, stern.length - ziffern.length)
  return (
    <span data-testid="kennung-stern" className={`font-mono font-bold tracking-tight text-ink ${gross ? 'text-[15px]' : 'text-[13px]'}`}>
      <span className="text-muted font-medium">{praefix}</span>{ziffern}
    </span>
  )
}

const SET_STIL = {
  einzel: 'border border-line text-muted',
  menge: 'bg-primary-soft text-primary',
  set: 'bg-warn-soft text-warn',
} as const

export function SetChip({ kennung }: { kennung: Kennung }) {
  const s = kennung.set
  const titel = s.art === 'set' ? `${s.teile} Teile aus ${s.komponenten.length} Artikeln` : s.art === 'menge' ? `${s.menge} gleiche Teile` : 'ein Stück'
  return (
    <span title={titel} className={`inline-block rounded px-1 py-px text-[10px] font-semibold leading-tight ${SET_STIL[s.art]}`}>
      {setText(s)}
    </span>
  )
}

/**
 * Artikel-Kennung. Mit Sternnummer: Stern groß, DR-Nummer klein darunter. Ohne Stern bleibt die DR-Nummer groß.
 * Die große Nummer führt zur Grundartikel-Seite: der Zeile selbst, ihres Grundartikels oder per Resolver.
 * Ohne Kennung (ältere Antwort) fällt sie auf SKU/Name zurück.
 */
export function ArtikelKennung({
  kennung, sku, name, mitName = true, kompakt = false, mitGrundartikel = true,
}: {
  kennung?: Kennung | null; sku?: string | null; name?: string | null; mitName?: boolean; kompakt?: boolean
  /** false, wenn der Grundartikel schon in einer eigenen Spalte steht. */
  mitGrundartikel?: boolean
}) {
  const dr = kennung?.sku ?? sku ?? null
  const titel = kennung?.name ?? name ?? null
  const stern = kennung?.stern ?? null
  const gross = stern ? <Stern stern={stern} gross={!kompakt} /> : (
    <span data-testid="kennung-dr-gross" className={`font-mono font-bold tracking-tight text-ink ${kompakt ? 'text-[13px]' : 'text-[15px]'}`}>{dr ?? '—'}</span>
  )
  return (
    <div className="leading-tight min-w-0" data-testid="artikel-kennung">
      <div className="flex flex-wrap items-baseline gap-x-1.5 gap-y-0.5">
        <GrundartikelLink code={stern ?? dr} className="decoration-ink">{gross}</GrundartikelLink>
        {kennung && <SetChip kennung={kennung} />}
        {kennung?.ist_grundartikel && (
          <span className="inline-block rounded px-1 py-px text-[10px] font-semibold leading-tight bg-ink text-panel">Grundartikel</span>
        )}
      </div>
      {(stern && dr) || (mitName && titel) ? (
        <div className="mt-0.5 text-[11px] text-muted truncate">
          {stern && dr && <span data-testid="kennung-dr" className="font-mono">{dr}</span>}
          {stern && dr && mitName && titel && <span aria-hidden> · </span>}
          {mitName && titel && <span title={titel}>{titel}</span>}
        </div>
      ) : null}
      {mitGrundartikel && kennung?.grundartikel && (
        <div className="mt-0.5 text-[11px] text-ink-2" title={[kennung.grundartikel.sku, kennung.grundartikel.name].filter(Boolean).join(' · ')}>
          <span aria-hidden className="text-muted">↳ </span>Grundartikel{' '}
          <GrundartikelLink
            code={kennung.grundartikel.stern ?? kennung.grundartikel.sku}
            className="font-mono font-semibold text-ink underline-offset-2 hover:underline focus-visible:outline-2 focus-visible:outline-primary"
          >
            {kennung.grundartikel.stern ?? kennung.grundartikel.sku}
          </GrundartikelLink>
        </div>
      )}
    </div>
  )
}

/** Versand-Politik: „inkl. Versand“ ruhig, „zzgl. Versand“ deutlich (Plus Versand ist bis A2 gesperrt). */
export function VersandChip({ versand }: { versand?: ShippingPolicy | null }) {
  if (!versand) return null
  return versand === 'exkl' ? (
    <span data-testid="versand-chip" className="inline-block rounded px-1.5 py-px text-[10px] font-bold leading-tight bg-warn-soft text-warn ring-1 ring-warn/40">
      zzgl. Versand
    </span>
  ) : (
    <span data-testid="versand-chip" className="inline-block rounded px-1.5 py-px text-[10px] font-semibold leading-tight bg-success-soft text-success">
      inkl. Versand
    </span>
  )
}

const WIRKUNG_STIL = {
  live: 'bg-coral text-ink',
  protokoll: 'bg-panel-2 text-ink-2 border border-line',
  aus: 'text-muted line-through decoration-muted/60',
} as const

/** Wirkung der Regel: live (schreibt echte Preise), nur Protokoll oder wirkt nicht mit Grund. */
export function WirkungChip({ wirkung }: { wirkung?: ScopeWirkung }) {
  const w = wirkungText(wirkung)
  if (!w) return <span className="text-muted">—</span>
  return (
    <span data-testid="wirkung" className={`inline-block rounded px-1.5 py-px text-[10px] font-semibold leading-tight ${WIRKUNG_STIL[w.ton]}`}>
      {w.text}
    </span>
  )
}

const ZEIT_STIL = { plan: 'text-ink', protokoll: 'text-ink-2', block: 'text-warn', fertig: 'text-success' } as const

/** „Nächster Schritt“: Termin → Preis (oder Grund), darunter Ziel voraussichtlich / Wartegrund. */
export function NaechsterSchritt({ zeitplan, praefix }: { zeitplan?: Zeitplan; praefix?: string }) {
  if (!zeitplan) return <span className="text-muted">—</span>
  const t = zeitplanText(zeitplan)
  return (
    <div className="leading-tight" data-testid="naechster-schritt">
      <div className={`text-xs ${t.ton === 'block' ? 'font-medium' : 'font-mono font-semibold'} ${ZEIT_STIL[t.ton]}`}>
        {praefix && <span className="font-sans font-normal text-muted">{praefix} </span>}
        {t.haupt}
      </div>
      {t.neben && <div className="text-[11px] text-muted mt-0.5">{t.neben}</div>}
    </div>
  )
}
